import { CustomerRepository } from "@bundles/crm/data"
import { getBundleParams } from "@bundles/crm/utils"
import {
    Box,
    ClickAwayListener,
    Icon,
    IconButton,
    LicensePlate,
    SearchField,
    styled,
    StyledSearchFieldBox,
    Table,
    TableCellData,
    TableColumnData,
    Typography,
} from "@tm/components"
import { useCountryCodeToLicensePlate, useUser, useWorkTask } from "@tm/context-distribution"
import { useLocalization } from "@tm/localization"
import { CustomerShortInfo } from "@tm/models"
import { concat, debounce } from "@tm/utils"
import { useCallback, useMemo, useRef, useState } from "react"

const SearchFieldBox = styled(StyledSearchFieldBox)<{ width?: number }>(({ width }) => ({
    width,
    overflow: "hidden",
}))

type Props = {
    onStartSearch(query: string): void
    onOpenCustomerDetails(customerShortInfo: CustomerShortInfo): void
    onAttachCustomer(customerShortInfo: CustomerShortInfo): void
    searchButtonIconOnly?: boolean
}

export function CustomerSearch(props: Props) {
    const { translateText, translate } = useLocalization()
    const { workTask } = useWorkTask() ?? {}
    const { userContext } = useUser()
    const { nextLight } = getBundleParams()
    const [searchQuery, setSearchQuery] = useState<string>("")
    const [suggestions, setSuggestions] = useState<CustomerShortInfo[] | undefined>(undefined)
    const [suggestionsOpen, setSuggestionsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const { plateCode } = useCountryCodeToLicensePlate(workTask?.vehicle?.countryCode)
    const searchFieldRef = useRef<HTMLDivElement>(null)
    const { onStartSearch, onOpenCustomerDetails, onAttachCustomer, searchButtonIconOnly } = props

    const debouncedRequest = useMemo(() => {
        return debounce(requestSuggestionsByQuery, 500)
    }, [])

    function renderVehicleDescription(data: CustomerShortInfo) {
        return data.model && [data.manufacturer, data.modelSeries, data.model].join(" ")
    }

    const suggestionColumns: TableColumnData[] = useMemo(() => {
        return [
            { header: translate(156) },
            { header: translate(155) },
            { header: `${translate(112)} / ${translate(113)}` },
            { header: translate(183) },
            { header: "" },
        ]
    }, [translate])

    const getCustomerCellData = useCallback(
        (customerShortInfo: CustomerShortInfo) => {
            const cellData: TableCellData[] = [
                {
                    id: "CustomerId",
                    displayValue: <Typography variant="label">{customerShortInfo.customerNo}</Typography>,
                },
                {
                    id: "Name",
                    displayValue: <Typography variant="label">{concat(" ", customerShortInfo.firstName, customerShortInfo.lastName)}</Typography>,
                },
                {
                    id: "ZIP",
                    displayValue: <Typography variant="label">{customerShortInfo.zip}</Typography>,
                },
                {
                    id: "Vehicle",
                    displayValue: (
                        <Box className="plate">
                            {!!customerShortInfo.plateId && (
                                <LicensePlate size="extrasmall" countryCode={plateCode} value={customerShortInfo.plateId} />
                            )}
                            <Box>{renderVehicleDescription(customerShortInfo)}</Box>
                        </Box>
                    ),
                },
                {
                    id: "Details",
                    displayValue: (
                        <IconButton onClick={() => onOpenCustomerDetails(customerShortInfo)}>
                            <Icon name="search" size="small" />
                        </IconButton>
                    ),
                },
            ]

            return cellData
        },
        [onOpenCustomerDetails, plateCode]
    )

    const displayData = useMemo(() => {
        return suggestions?.map((suggestion, index) => ({ cells: getCustomerCellData(suggestion), id: `${index}`, customRow: false, active: false }))
    }, [getCustomerCellData, suggestions])

    function requestSuggestionsByQuery(query: string) {
        if (query === "") {
            setSuggestions(undefined)
            return
        }

        setIsLoading(true)
        CustomerRepository.findCustomers({ query })
            .then((response) => {
                setIsLoading(false)
                setSuggestions(response?.customers)
            })
            .catch(() => {
                setIsLoading(false)
            })
    }

    return (
        <ClickAwayListener onClickAway={() => setSuggestionsOpen(false)}>
            <Box>
                <SearchField
                    fullWidth
                    value={searchQuery}
                    onChange={(value) => {
                        debouncedRequest(value)
                        setSearchQuery(value)
                    }}
                    onStartSearch={() => onStartSearch(searchQuery)}
                    placeholder={translateText(184)}
                    className="customer"
                    buttonTitle={!searchButtonIconOnly ? translateText(135) : undefined}
                    disabled={userContext?.parameter.catalogLight || nextLight}
                    ref={searchFieldRef}
                    isLoading={isLoading}
                    onFocus={() => setSuggestionsOpen(true)}
                    tooltip={translateText(572)}
                >
                    {searchFieldRef && suggestionsOpen && suggestions && suggestions.length > 0 && (
                        <SearchFieldBox width={searchFieldRef.current?.offsetWidth}>
                            <Table
                                columns={suggestionColumns}
                                rows={displayData}
                                onRowClick={(data) => {
                                    onAttachCustomer(suggestions[parseInt(data.id)])
                                }}
                            />
                        </SearchFieldBox>
                    )}
                </SearchField>
            </Box>
        </ClickAwayListener>
    )
}
